"use client";
import React, { useState, useEffect, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import {
  MousePointerClick,
  Code,
  ArrowUpDown,
  CheckCircle,
  Users,
} from "lucide-react";
import Safari from "./safari";
import { Badge } from "@/components/ui/badge";

interface StepData {
  id: number;
  title: string;
  content: string;
  image: string;
  icon: ReactNode;
  stat: string;
}

type NavigationStyle = "horizontal" | "vertical";

const data: StepData[] = [
  {
    id: 1,
    title: "AI Writing Assistant",
    content:
      "Select from a rich library of pre-built form components. Customize and combine them effortlessly to create forms tailored to your needs.",
    image: "/dummy-image.svg",
    icon: <MousePointerClick className="w-6 h-6" />,
    stat: "80% faster form setup",
  },
  {
    id: 2,
    title: "Automation",
    content:
      "Easily add new form fields or rearrange existing ones. Our intuitive drag-and-drop interface lets you structure your form exactly the way you want.",
    image: "/dummy-image.svg",
    icon: <ArrowUpDown className="w-6 h-6" />,
    stat: "100% customizable layouts",
  },
  {
    id: 3,
    title: "Social Inbox",
    content:
      "Define how your form should behave. Configure validation rules, set SSR options, and choose your validation library for maximum flexibility.",
    image: "/dummy-image.svg",
    icon: <Code className="w-6 h-6" />,
    stat: "50% fewer validation bugs",
  },
  {
    id: 4,
    title: "Collaboration & Approval",
    content:
      "Launch your form and start collecting data immediately. Track submissions in real-time and export data in multiple formats.",
    image: "/dummy-image.svg",
    icon: <Users className="w-6 h-6" />,
    stat: "95% submission completion rate",
  },
];

export default function HowItWorks() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [navStyle, setNavStyle] = useState<NavigationStyle>("horizontal");

  // Switch between navigation styles based on screen width
  useEffect(() => {
    const handleResize = () => {
      setNavStyle(window.innerWidth < 768 ? "vertical" : "horizontal");
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <section className="pb-20 pt-20 md:pb-32 md:pt-32 container mx-auto">
      <div className="text-center space-y-4 pb-16 mx-auto max-w-4xl">
        <Badge>HOW IT WORKS</Badge>
        <h2 className="mx-auto mt-4 text-3xl font-bold sm:text-5xl tracking-tight">
          Technology-first approach
        </h2>
        <p className="text-xl text-muted-foreground pt-1">
          Managing a small business today is already tough. Avoid further
          complications by ditching outdated, tedious trade methods.
        </p>
      </div>

      <div>
        {/* Horizontal Step Navigation (Desktop) */}
        {navStyle === "horizontal" && (
          <div className="flex justify-center mb-12">
            <div className="flex items-center space-x-8 p-1">
              {data.map((step, index) => (
                <button
                  key={step.id}
                  onClick={() => setCurrentIndex(index)}
                  className={cn(
                    "flex items-center space-x-3 px-3 py-3 text-base font-medium transition-all duration-200 border-b-2",
                    currentIndex === index
                      ? "border-b-primary text-foreground"
                      : "border-b-transparent text-muted-foreground hover:text-foreground"
                  )}
                >
                  <div
                    className={cn(
                      "size-6",
                      currentIndex === index
                        ? "text-primary"
                        : "text-muted-foreground"
                    )}
                  >
                    {step.icon}
                  </div>
                  <span className="">{step.title}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Vertical Step Navigation (Mobile & Tablet) */}
          {navStyle === "vertical" && (
            <div className="lg:col-span-3 block">
              <div className="flex flex-col space-y-1">
                {data.map((step, index) => (
                  <button
                    key={step.id}
                    onClick={() => setCurrentIndex(index)}
                    className={cn(
                      "flex items-center space-x-3 p-3 text-left transition-all duration-200 border-l-2",
                      currentIndex === index
                        ? "border-l-primary text-foreground"
                        : "border-l-transparent text-muted-foreground hover:text-foreground"
                    )}
                  >
                    <div className="flex-shrink-0 size-8 flex items-center justify-center">
                      <div
                        className={cn(
                          "size-5",
                          currentIndex === index
                            ? "text-primary"
                            : "text-muted-foreground"
                        )}
                      >
                        {step.icon}
                      </div>
                    </div>
                    <div>
                      <p className="font-medium">{step.title}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Content Side */}
          <div
            className={cn(
              "space-y-8",
              navStyle === "vertical" ? "lg:col-span-9" : "lg:col-span-6"
            )}
          >
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                  {data[currentIndex].icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">
                    {data[currentIndex].title}
                  </h3>
                  <p className="text-lg text-muted-foreground leading-relaxed">
                    {data[currentIndex].content}
                  </p>
                </div>
              </div>

              {/* Stat Card */}
              <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-6 border border-primary/20">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                    <CheckCircle className="size-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">
                      {data[currentIndex].stat}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Proven results
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Visual Side */}
          <div
            className={cn(
              "flex items-center justify-center",
              navStyle === "vertical" ? "lg:col-span-9" : "lg:col-span-6"
            )}
          >
            <div className="w-full max-w-lg relative">
              {/* Gradient background for the Safari component */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-primary/5 rounded-2xl blur-3xl" />

              <div className="relative">
                <Safari
                  src={data[currentIndex].image}
                  className="w-full h-auto rounded-xl border shadow-2xl shadow-primary/10"
                />
              </div>

              {/* Step indicator dots */}
              <div className="flex justify-center space-x-2 mt-6">
                {data.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all duration-200",
                      currentIndex === index
                        ? "bg-primary w-6"
                        : "bg-muted-foreground/30"
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
